import {BRAND_NAME} from "../../../config/_variable";

// One build serves every brand, so the manifest is chosen by host at boot instead of being baked
// into index.html. Brands without a file fall back to no manifest at all: a wrong name or icon on
// someone's dock is worse than no install offer.
//
// BRAND_NAME -> the folder holding its manifest and icons, and the app name in that manifest (the
// install offer shows the name that will sit in the Dock, not the legal company name). Only the
// brands in use have one: blueocean (layout 1), antospnc (layout 2), pxmarkets (layout 3). The old
// "antos" hosts share the Antos Pinnacles assets. `manifest` names the brand's own manifest file
// when it differs from the icon folder in `assets`: apinternational borrows the antospnc icons but
// installs under its own name (AP-153).
// themeColor is each palette's --header-bg-color. Chrome takes the page's <meta name="theme-color">
// over the manifest for the installed window's title bar, so the meta follows the palette on screen;
// the manifest's theme_color (the splash before the page loads) is the dark one, the brands' default.
const MANIFEST_ASSETS = {
    blueocean: {assets: "blueocean", name: "Blue Ocean Financials", themeColor: {dark: "#161923", light: "#FFFFFF"}},
    antos: {assets: "antospnc", name: "Antos Pinnacles", themeColor: {dark: "#0B0E11", light: "#FBFBFB"}},
    antospnc: {assets: "antospnc", name: "Antos Pinnacles", themeColor: {dark: "#0B0E11", light: "#FBFBFB"}},
    // TODO(AP-153): antospnc's icons and colours until AP International's own artwork lands
    apinternational: {assets: "antospnc", manifest: "apinternational", name: "AP International", themeColor: {dark: "#0B0E11", light: "#FBFBFB"}},
    pxmarkets: {assets: "pxmarkets", name: "Prorex", themeColor: {dark: "#08101C", light: "#FFFFFF"}},
};

export const MANIFEST_BRANDS = Object.keys(MANIFEST_ASSETS);

const entryFor = (brand) =>
    Object.prototype.hasOwnProperty.call(MANIFEST_ASSETS, brand) ? MANIFEST_ASSETS[brand] : null;

const assetsFor = (brand) => entryFor(brand)?.assets || null;

export const appNameFor = (brand) => entryFor(brand)?.name || null;

export const themeColorFor = (brand, palette = "dark") =>
    entryFor(brand)?.themeColor?.[palette === "light" ? "light" : "dark"] || null;

/** Points the page's theme-color at the palette on screen. Brands without assets keep index.html's. */
export const applyThemeColor = (brand, palette) => {
    const color = themeColorFor(brand, palette);

    if (!color) return;

    let meta = document.querySelector('meta[name="theme-color"]');

    if (!meta) {
        meta = document.createElement("meta");
        meta.name = "theme-color";
        document.head.appendChild(meta);
    }

    meta.content = color;
};

// dev only: lets one localhost serve every brand's manifest without touching the host map
const devOverride = () => {
    if (process.env.NODE_ENV === "production") return null;

    try {
        return new URLSearchParams(window.location.search).get("pwaBrand");
    } catch (error) {
        return null;
    }
};

// the brand whose manifest is on the page; null means nothing here can be installed properly
let activeBrand = null;

export const getActiveManifestBrand = () => activeBrand;

export const appIconFor = (brand) => (assetsFor(brand) ? `/brand/icons/${assetsFor(brand)}/icon-192.png` : null);

export const manifestHrefFor = (brand) =>
    assetsFor(brand) ? `/manifest/${entryFor(brand).manifest || assetsFor(brand)}.json` : null;

export const appleTouchIconFor = (brand) =>
    assetsFor(brand) ? `/brand/icons/${assetsFor(brand)}/apple-touch-icon-180.png` : null;

// Runs before React mounts: Chrome decides installability from the manifest link it sees, and
// beforeinstallprompt can fire before the first render.
export const injectManifest = (brand = devOverride() || BRAND_NAME) => {
    const href = manifestHrefFor(brand);

    if (!href) {
        activeBrand = null;
        return null;
    }

    let link = document.querySelector('link[rel="manifest"]');

    if (!link) {
        link = document.createElement("link");
        link.rel = "manifest";
        document.head.appendChild(link);
    }

    link.href = href;
    activeBrand = brand;

    // dark until App knows the palette, matching the manifest
    applyThemeColor(brand, "dark");

    return brand;
};
